/**
 * Tax Report - التقرير الضريبي
 * صفحة فارغة حالياً، سيتم تعبئتها لاحقاً حسب متطلبات المستخدم
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';

const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

export default function TaxReport() {
  const { language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#030303] relative" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="tax-report-page">
      {/* Background Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton={true} backPath="/dashboard/finance" />

      {/* Breadcrumb */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
              data-testid="breadcrumb-dashboard"
            >
              {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
            </button>
            <span className="text-[#71717A]">{language === 'ar' ? '>' : '<'}</span>
            <button
              onClick={() => navigate('/dashboard/finance')}
              className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
              data-testid="breadcrumb-finance"
            >
              {language === 'ar' ? 'المالية' : 'Finance'}
            </button>
            <span className="text-[#71717A]">{language === 'ar' ? '>' : '<'}</span>
            <span className="text-[#d4af37]">
              {language === 'ar' ? 'التقرير الضريبي' : 'Tax Report'}
            </span>
          </div>
        </div>
      </div>

      {/* Main */}
      <main className="relative z-10 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-20 h-20 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center mx-auto mb-6"
            >
              <FileText className="h-10 w-10 text-[#d4af37]" />
            </motion.div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {language === 'ar' ? 'التقرير الضريبي' : 'Tax Report'}
            </h1>
            <p className="text-[#71717A] text-lg">
              {language === 'ar' ? 'التقارير والإقرارات الضريبية' : 'Tax Reports & Declarations'}
            </p>
          </div>

          {/* Empty State Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-12 text-center"
            data-testid="tax-report-empty-state"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                <FileText className="h-8 w-8 text-[#71717A]" />
              </div>
              <h3 className="text-xl font-bold text-white">
                {language === 'ar' ? 'قيد التطوير' : 'Under Development'}
              </h3>
              <p className="text-[#71717A] max-w-md">
                {language === 'ar'
                  ? 'سيتم تفعيل التقارير الضريبية قريباً. هذا القسم قيد البناء حسب متطلباتك.'
                  : 'Tax reporting will be activated soon. This section is being built according to your requirements.'}
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer Logo */}
      <div className="flex justify-center pb-12">
        <img src={LOGO_TRIANGLE} alt="ASB606" className="w-16 h-16 object-contain opacity-20" />
      </div>
    </div>
  );
}
